/**
 * @copyright WaterStreet. All rights reserved.
*/

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Injectable,
} from '@angular/core';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	EntityTypeApiService
} from '@api/services/entities/entity-type.api.service';
import {
	OperationDefinitionApiService
} from '@api/services/operations/operation-definition.api.service';
import {
	EntityInstanceComponent
} from '@entity/components/entity-instance/entity-instance.component';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	IPrintMenuReport
} from '@insurance/interfaces/print-menu-report.interface';
import {
	IPrintMenu
} from '@insurance/interfaces/print-menu.interface';
import {
	InsuranceService
} from '@insurance/services/insurance.service';
import {
	BaseOperationAction
} from '@operation/actions/base/base-operation-action';
import {
	IOperationDefinition
} from '@operation/interfaces/operation-definition.interface';
import {
	IOperationTypeParameter
} from '@operation/interfaces/operation-type-parameter.interface';
import {
	IOperationType
} from '@operation/interfaces/operation-type.interface';
import {
	OperationExecutionService
} from '@operation/services/operation-execution.service';
import {
	OperationService
} from '@operation/services/operation.service';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	AppCanDeactivateGuard
} from '@shared/guards/app-can-deactivate.guard';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	ApiHelper
} from '@shared/helpers/api.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	IEntityType
} from '@shared/interfaces/entities/entity-type.interface';
import {
	ActivityService
} from '@shared/services/activity.service';
import {
	JSONPath
} from 'jsonpath-plus';
import {
	MenuItem
} from 'primeng/api';

/* eslint-enable max-len */

/**
 * A class representing the action to dynamically populate
 * the print menu with reports configured on the product.
 *
 * @export
 * @class AddDynamicPrintMenuAction
 * @extends {BaseOperationAction}
 */
@Injectable()
export class AddDynamicPrintMenuAction
	extends BaseOperationAction
{
	/**
	 * Creates an instance of a AddDynamicPrintMenuAction.
	 *
	 * @param {ActivityService} activityService
	 * The activity service used for this action.
	 * @param {EntityTypeApiService} entityTypeApiService
	 * The entity type api service used for this action.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service used for this action.
	 * @param {InsuranceService} insuranceService
	 * The insurance service used for this action.
	 * @param {OperationExecutionService} operationExecutionService
	 * The operation execution service used for this action.
	 * @param {OperationService} operationService
	 * The operation service used for this action.
	 * @param {OperationDefinitionApiService} operationDefinitionApiService
	 * The operation definition api service used for this action.
	 * @param {AppCanDeactivateGuard} appCanDeactivateGuard
	 * The app can deactivate guard.
	 * @memberof AddDynamicPrintMenuAction
	 */
	public constructor(
		public activityService: ActivityService,
		public entityTypeApiService: EntityTypeApiService,
		public entityInstanceApiService: EntityInstanceApiService,
		public insuranceService: InsuranceService,
		protected operationExecutionService: OperationExecutionService,
		protected operationService: OperationService,
		protected operationDefinitionApiService: OperationDefinitionApiService,
		protected appCanDeactivateGuard: AppCanDeactivateGuard)
	{
		super(
			activityService,
			operationExecutionService,
			operationService,
			operationDefinitionApiService,
			appCanDeactivateGuard);
	}

	/**
	 * Gets or sets the operation name.
	 *
	 * @type {string}
	 * @memberof AddDynamicPrintMenuAction
	 */
	public readonly operationName: string =
		'AddDynamicPrintMenu';

	/**
	 * Gets or sets the query string for the PrintFile action command.
	 *
	 * @type {string}
	 * @memberof AddDynamicPrintMenuAction
	 */
	public readonly queryString: string =
		'return function() { '
			+ 'return "?generateIfNotExisting=true&useExisting=false"; }';

	/**
	 * Gets or sets the view for the PrintFile action command.
	 *
	 * @type {string}
	 * @memberof AddDynamicPrintMenuAction
	 */
	public readonly view: string =
		'open';

	/**
	 * Gets or sets the full set of entity types.
	 *
	 * @type {IEntityType[]}
	 * @memberof AddDynamicPrintMenuAction
	 */
	public entityTypes: IEntityType[] = [];

	/**
	 * Gets or sets the name of the operation action to be used
	 * in the command for each report.
	 *
	 * @type {string}
	 * @memberof AddDynamicPrintMenuAction
	 */
	public readonly reportCommandActionName: string =
		'PrintFile';

	/**
	 * Gets or sets the post operation action name for the menu item command.
	 *
	 * @type {string}
	 * @memberof AddDynamicPrintMenuAction
	 */
	public readonly postOperationAction: string =
		'EmitRefreshFilesComponentEvent';

	/**
	 * Gets or sets the id.
	 *
	 * @type {number}
	 * @memberof AddDynamicPrintMenuAction
	 */
	public id: number;

	/**
	 * Gets or sets the entity instance type group.
	 *
	 * @type {string}
	 * @memberof AddDynamicPrintMenuAction
	 */
	public typeGroup: string = AppConstants.empty;

	/**
	 * Gets or sets the path to the print menu object in the product instance.
	 *
	 * @type {string}
	 * @memberof AddDynamicPrintMenuAction
	 */
	public path: string = AppConstants.empty;

	/**
	 * Gets or sets the print menu object.
	 *
	 * @type {IPrintMenu}
	 * @memberof AddDynamicPrintMenuAction
	 */
	public printMenu: IPrintMenu;

	/**
	 * Gets or sets the reports object
	 *
	 * @type {IPrintMenuReport[]}
	 * @memberof AddDynamicPrintMenuAction
	 */
	public reports: IPrintMenuReport[] = [];

	/**
	 * Executes the defined action.
	 *
	 * @async
	 * @returns {Promise<MenuItem[]>}
	 * The menu item array of mapped related objects or entities.
	 * @memberof AddDynamicPrintMenuAction
	 */
	public async execute(): Promise<MenuItem[]>
	{
		if (this.pageContext == null)
		{
			return [];
		}

		this.setParameters();

		const entityInstance: IEntityInstance =
			await this.getEntity();

		if (!AnyHelper.isNull(entityInstance))
		{
			const productInstance: IEntityInstance =
				await this.getProduct(entityInstance);

			this.printMenu =
				this.getPrintMenu(
					this.path,
					productInstance);

			if(this.printMenu.enabled === true)
			{
				this.reports =
					this.printMenu.reports;
			}
		}

		return this.populateMenuItems();
	}

	/**
	 * Gets the product via the passed in entity. Right now this logic
	 * only handles getting the product from the policy term and policy
	 * term transaction.
	 *
	 * @async
	 * @param {IEntityInstance} entity
	 * The entity that this operation relates to.
	 * @returns {Promise<IEntityInstance>}
	 * A menu item array holding the related items to the sent page context.
	 * @memberof AddDynamicPrintMenuAction
	 */
	public async getProduct(
		entity: IEntityInstance): Promise<IEntityInstance>
	{
		let product: IEntityInstance;
		// Expand logic below for getting the product on different entities.
		if (entity.entityType
			=== InsuranceConstants
				.insuranceEntityTypes
				.policyTerm)
		{
			product =
				await this.insuranceService
					.getProductByName(entity.data.productName);
		}
		else
		{
			product =
				await this.insuranceService
					.getProductByTransaction(entity);
		}

		return product;
	}

	/**
	 * Gets the print menu object from the product instance
	 * by using JSONPath with the supplied path string.
	 *
	 * @async
	 * @param {string} path
	 * The path sent in from configuration to get the printMenu from.
	 * @param {string} product
	 * The product instance.
	 * @returns {IPrintMenu}
	 * A print menu object obtained from the path string.
	 * @memberof AddDynamicPrintMenuAction
	 */
	public getPrintMenu(
		path: string,
		product: IEntityInstance): IPrintMenu
	{
		if (AnyHelper.isNull(product))
		{
			return null;
		}

		return JSONPath(
			{
				path: path,
				json: product
			})[0];
	}

	/**
	 * Gets the entity instance from the typeGroup found through the
	 * entity type api service, and id.
	 *
	 * @async
	 * @returns {Promise<IEntityInstance>}
	 * The entity instance.
	 * @memberof AddDynamicPrintMenuAction
	 */
	public async getEntity(): Promise<IEntityInstance>
	{
		this.entityTypes =
			await ApiHelper.getFullDataSet(
				this.entityTypeApiService,
				AppConstants.empty,
				AppConstants.empty);

		const entityType: IEntityType =
			this.entityTypes.find(
				(type: IEntityType) =>
					type.name === this.typeGroup);

		if(AnyHelper.isNull(entityType))
		{
			return null;
		}

		this.entityInstanceApiService.entityInstanceTypeGroup =
			entityType.group;

		return this.entityInstanceApiService
			.get(Number(this.id));
	}

	/**
	 * Populates menu items for each report found in the
	 * print menu. Assigning the PrintFile action as the command.
	 *
	 * @async
	 * @returns {Promise<MenuItem[]>}
	 * A menu item array holding the configured reports.
	 * @memberof AddDynamicPrintMenuAction
	 */
	public async populateMenuItems(): Promise<MenuItem[]>
	{
		const menuItems: MenuItem[] = [];
		let order: number = 0;

		for (const report of this.reports)
		{
			order += 10;

			const reportLabel: string =
				StringHelper.interpolate(
					report.name,
					this.pageContext);

			const isVisible: boolean =
				!AnyHelper.isNullOrWhitespace(report.visible)
					? await StringHelper
						.transformAndExecuteBooleanPromise(
							report.visible,
							this.pageContext)
					: true;
			const isEnabled: boolean =
				!AnyHelper.isNullOrWhitespace(report.enabled)
					? await StringHelper
						.transformAndExecuteBooleanPromise(
							report.enabled,
							this.pageContext)
					: true;

			const successMesssage: string =
				!AnyHelper.isNullOrWhitespace(report.successMessage)
					? report.successMessage
					: 'File has been generated';

			const actionName: string =
				!AnyHelper.isNullOrEmpty(report.actionName)
					? report.actionName
					: null;

			let disabledMessage: string;
			if (isEnabled === false)
			{
				if (!AnyHelper.isNullOrEmpty(report.disabledMessagePromise))
				{
					disabledMessage =
						await StringHelper.transformToDataPromise(
							report.disabledMessagePromise,
							this.pageContext);
				}
			}

			const reportParams: IOperationTypeParameter[] =
				[
					<IOperationTypeParameter>
					{
						name: 'successMessage',
						definitionParameterValue: successMesssage
					},
					<IOperationTypeParameter>
					{
						name: 'actionName',
						definitionParameterValue: actionName
					},
					<IOperationTypeParameter>
					{
						name: 'queryString',
						definitionParameterValue: this.queryString
					},
					<IOperationTypeParameter>
					{
						name: 'view',
						definitionParameterValue: this.view
					},
					<IOperationTypeParameter>
					{
						name: 'postOperationAction',
						definitionParameterValue: this.postOperationAction
					},
				];

			const operationType: IOperationType =
				<IOperationType>
				{
					name: this.reportCommandActionName
				};

			const reportDefinition: IOperationDefinition =
				<IOperationDefinition>
				{
					operationType: operationType,
					operationTypeParameters: reportParams,
					label: reportLabel,
					icon: null,
					order: order,
					pageContext: this.pageContext
				};

			const currentReport: MenuItem =
				<MenuItem>
				{
					label: reportLabel,
					icon: null,
					id: this.operationName
						+ AppConstants.characters.leftSquareBracket
						+ report.name
						+ AppConstants.characters.rightSquareBracket,
					visible: isVisible,
					disabled: !isEnabled,
					items: null,
					tooltipOptions: {
						tooltipLabel: disabledMessage
					},
					command: async(_event: Event) =>
					{
						setTimeout(
							() => {
								currentReport.disabled = true;
							});

						try
						{
							await this.operationExecutionService
								.executeMappedOperation(
									reportDefinition);
							setTimeout(
								() => {
									currentReport.disabled = !isEnabled;
								});
						}
						catch (exception)
						{
							setTimeout(
								() => {
									currentReport.disabled = !isEnabled;
								});

							throw exception;
						}
					}
				};
			menuItems.push(currentReport);
		}

		return menuItems;
	}

	/**
	 * Sets the parameters for the action.
	 *
	 * @returns {void}
	 * An empty promise.
	 * @memberof AddDynamicPrintMenuAction
	 */
	private setParameters(): void
	{
		const contextEntity: EntityInstanceComponent =
			this.pageContext.source as EntityInstanceComponent;

		this.typeGroup =
			!AnyHelper.isNullOrWhitespace(this.typeGroup)
				? StringHelper.interpolate(
					this.typeGroup,
					this.pageContext)
				: contextEntity.entityInstance.entityType;

		this.id =
			!AnyHelper.isNullOrWhitespace(this.id)
				? Number(StringHelper.interpolate(
					this.id.toString(),
					this.pageContext))
				: contextEntity.entityInstance.id;
	}
}